import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal, { Header } from 'components/Modal';
import {
  MCPOSTMAN_FORM_ACTION,
  MCPOSTMAN_PLUS_FORM_ACTION,
  SFMC_FORM_ACTION,
  SUBSCRIPTION_FORM_PROXY,
} from 'config/subscriptionForms';
import sty from './styles.module.css';

// Intercepts submissions of legacy McPostman subscription forms (rendered
// from CMS body content) and relays them to the Marketing Cloud form handler
// via our proxy route. Matches actions exactly so other forms are untouched.
const INTERCEPTED_ACTIONS = [
  MCPOSTMAN_FORM_ACTION,
  MCPOSTMAN_PLUS_FORM_ACTION,
  `${MCPOSTMAN_FORM_ACTION}/`,
  SFMC_FORM_ACTION,
];

const SubscriptionFormInterceptor: FunctionComponent = () => {
  const [showModal, setShowModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement;
      const action = form.getAttribute('action') ?? '';
      if (!INTERCEPTED_ACTIONS.includes(action)) return;

      event.preventDefault();

      const submitter = (event as SubmitEvent).submitter;
      if (submitter) submitter.setAttribute('disabled', 'true');

      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        params.append(key, value.toString());
      });

      fetch(SUBSCRIPTION_FORM_PROXY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })
        .then(async (response) => {
          if (response.ok) {
            // Leave the submitted values visible but grey out and disable
            // the form so it can't be submitted twice.
            Array.from(form.elements).forEach((element) => {
              element.setAttribute('disabled', 'true');
            });
            form.style.opacity = '0.5';
            setErrorMessage('');
            setHasError(false);
          } else {
            const data = await response.json().catch(() => undefined);
            setErrorMessage(data?.error ?? '');
            setHasError(true);
            if (submitter) submitter.removeAttribute('disabled');
          }
          setShowModal(true);
        })
        .catch((e) => {
          console.error(e);
          setErrorMessage('');
          setHasError(true);
          if (submitter) submitter.removeAttribute('disabled');
          setShowModal(true);
        });
    };

    document.addEventListener('submit', handleSubmit);
    return () => document.removeEventListener('submit', handleSubmit);
  }, []);

  if (!showModal) return null;

  return (
    <Modal show={showModal} onClose={() => setShowModal(false)}>
      <div className={sty.modal}>
        <Header
          title={hasError ? 'Something went wrong' : 'Thank you!'}
          className={sty.modal_header}
          onClose={() => setShowModal(false)}
        />
        <p>
          {hasError
            ? errorMessage ||
              'We were unable to submit the form. Please check that all required fields are filled out and try again.'
            : 'Thank you for your form submission.'}
        </p>
      </div>
    </Modal>
  );
};

export default SubscriptionFormInterceptor;
